/**
 * Renders the saved list. Reads through djh-wishlist.js rather than touching storage or
 * the proxy itself, so the store, its guards and the sync live in one file.
 *
 * 🔴 ONE CARD PER PIECE, SIZES AS CHIPS INSIDE IT. The store holds a row per
 * (piece, size) — that is the right shape, because "she saved this in her size" and "she
 * saved this" are different facts. But rendering it row-per-row shows the same ring three
 * times, and a customer reads that as a bug in our list rather than as three deliberate
 * saves.
 *
 * GROUPING ALSO SETTLES WHAT REMOVE MEANS. A chip removes the one variant it can name;
 * the card removes the piece. Those are the only two removals a customer has a word for,
 * so they are the only two this page offers.
 *
 * The route does carry a third — a named `'sizeless'` scope — because `variantId: null`
 * and an absent `variantId` are identical JSON meaning opposite things, so OPS named the
 * scopes rather than leaving it inferred.
 *
 * Nothing here calls it, and as of migration 20260906080000 nothing CAN: hearting a piece
 * that is already saved now refines the existing row instead of adding a sizeless one, so
 * a sizeless row and a sized row can no longer coexist for one handle by any path through
 * the doors. Grouping was already the right shape on ergonomics; it is now also the only
 * shape the data can take. Noted because the omission would otherwise read as an oversight
 * — and because whoever changes the merge semantics next needs to know this depended on
 * them.
 *
 * EACH PIECE IS FETCHED FRESH. What is stored is a pointer — handle, variant, when — so a
 * price here is the price now, not the price on the day it was saved. A stored price is a
 * price that will one day be wrong on a page the customer trusts.
 *
 * ⚠️ A PIECE THAT NO LONGER RESOLVES SAYS SO. Unpublished, renamed, gone from the
 * catalogue — whatever the reason, it stays in the list with an honest line rather than
 * disappearing. Quietly dropping something a customer deliberately saved is the same
 * failure as a heart that does not stick, and it is worse because nobody can see it happen.
 *
 * ⚠️ `savedVia: 'counter'` IS MARKED. A piece a salesperson noted appearing in a
 * customer's own list, unexplained, reads as surveillance. Named, it reads as service —
 * and it is the same fact either way.
 */
(() => {
  const grid = document.querySelector('[data-djh-wishlist-grid]');
  const empty = document.querySelector('[data-djh-wishlist-empty]');
  // The account ask moved into the subheading; the bordered box at the foot is gone.
  const signin = document.querySelector('[data-djh-wishlist-signin-line]');
  const note = document.querySelector('[data-djh-wishlist-note]');
  if (!grid || !empty) return;

  // ⚠️ A BARE `if (!store) return` IS HOW THIS PAGE WENT BLANK. djh-wishlist.js is loaded
  // globally and this script is loaded by the section, so if the two ever swap order
  // again the store is simply not there yet — and returning quietly renders a page with
  // no grid and no empty state, beside a header badge that says how many are saved.
  // Waiting costs nothing and cannot be got wrong by moving a script tag.
  if (!window.djhWishlist) {
    document.addEventListener('djh:wishlist:ready', start, { once: true });
    return;
  }
  start();

  function start() {
  const store = window.djhWishlist;

  const text = (el, key) => (el && el.dataset[key]) || '';

  // The price a saved piece may show. `/products/<h>.js` has no metafields, so its `price` can be an ESTIMATE (D-220); the
  // djh-wishlist-price section says what every other surface says, from djh-confirmed-price: "min|max|count" in cents.
  // Anything unreadable is "price on request", never the raw price (fail closed, as the snippet does).
  const confirmedByHandle = new Map(); // a render runs several times per load; one request per piece, not one per render
  const withConfirmedPrice = (product) => {
    if (!confirmedByHandle.has(product.handle)) confirmedByHandle.set(product.handle, fetchConfirmedPrice(product));
    return confirmedByHandle.get(product.handle).then((cents) => { product.djhPriceCents = cents; return product; });
  };
  const fetchConfirmedPrice = (product) =>
    fetch('/products/' + encodeURIComponent(product.handle) + '?section_id=djh-wishlist-price', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        const m = /data-djh-confirmed-price="(\d+)\|(\d+)\|(\d+)"/.exec(html);
        return m && Number(m[3]) > 0 ? Number(m[1]) : null;
      })
      .catch(() => null);
  const priceText = (product) => (
    product.djhPriceCents != null ? money(product.djhPriceCents) : (text(grid, 'priceOnRequest') || 'Price on request'));

  const money = (cents) =>
    (window.Shopify && window.Shopify.formatMoney)
      ? window.Shopify.formatMoney(cents)
      : (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }); // "$5,308.60" — toFixed dropped the comma (2026-09-24)

  const el = (tag, className, content) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  };

  /** Group the flat rows into one entry per piece, preserving the newest save time. */
  const group = (rows) => {
    const pieces = new Map();
    rows.forEach((row) => {
      if (!pieces.has(row.handle)) pieces.set(row.handle, { handle: row.handle, at: row.at, rows: [] });
      const piece = pieces.get(row.handle);
      piece.rows.push(row);
      if (row.at && (!piece.at || row.at > piece.at)) piece.at = row.at;
    });
    return Array.from(pieces.values()).sort((a, b) => (b.at || 0) > (a.at || 0) ? 1 : -1);
  };

  // 🔴 "Default Title" IS NOT A SIZE AND MUST NEVER BE A CHIP. Shopify gives a product
  // with no real options one variant titled "Default Title" on an option named "Title".
  // The PDP already refuses to render that as a spec row — `has_only_default_variant` is
  // the flag — and the wishlist did not, so a saved necklace showed a chip reading
  // literally "Default Title". Measured on monstera-design-mom-necklace after the heart
  // started recording the live variant: nothing had changed except that this row now HAD
  // a variant to describe, which is how a latent bug becomes a visible one.
  //
  // The row still saves the variant — the cart needs it — it simply has nothing to say
  // about a choice the customer was never offered.
  const isPlaceholderVariant = (variant, product) =>
    !!variant
    && (variant.title === 'Default Title'
        || (product && product.variants && product.variants.length === 1
            && (!product.options || product.options.length === 0
                || String((product.options[0] || {}).name || product.options[0]) === 'Title')));

  // ── how one option reads on a chip ─────────────────────────────────────────────
  //
  // "14K rose gold · 25.4 × 2.62 mm", not "Metal 14K Rose Gold · Center Dimensions (LxW)
  // 25.4 X 2.62 Mm". The axis NAME stays only where the value alone means nothing — a bare
  // "7" is not a sentence (it was once a whole chip reading "5 ×"), so sizes keep "Size".
  // Shopify names a single unnamed axis "Title"; that is never printed. Numbers are shown as
  // the catalog states them — never rounded, since a chip that disagrees with the page is worse
  // than a long one.
  const optionWords = (name, value) => {
    const v = String(value == null ? '' : value).trim();
    if (!v) return '';
    const axis = String(name || '');
    if (/size/i.test(axis)) return 'Size ' + v;
    return v
      .replace(/(\d)\s*[xX×]\s*(?=\d)/g, '$1 × ')
      .replace(/\bmm\b/gi, 'mm')
      .split(' ')
      .map((word, i) => (i > 0 && /^[A-Z][a-z]+$/.test(word) ? word.toLowerCase() : word))
      .join(' ');
  };

  const chip = (piece, row, product, removable) => {
    const variant = product && product.variants
      ? product.variants.find((v) => String(v.id) === row.variant)
      : null;

    const wrap = el('span', 'djh-wishlist__chip');
    // ⚠️ THE VARIANT TITLE ALONE IS NOT A LABEL. It rendered as a bare "5" — the whole
    // chip read `5 ×`, which tells a customer nothing about what five is. The option
    // NAME is what makes it a sentence, and it is the shop's own word for the axis
    // ("Ring Size 5", not "Size 5"), so it comes from the product rather than from a
    // string written here.
    //
    // Falls back to the variant title, then to a generic label: a variant we cannot
    // name still gets a chip, because the customer chose SOMETHING and dropping the row
    // would quietly discard it.
    let label = variant ? variant.title : '';
    if (variant && product.options && Array.isArray(variant.options)) {
      const named = variant.options
        .map((value, i) => {
          // ⚠️ `product.options` IS NOT THE SAME SHAPE HERE AS IN LIQUID. The .js
          // endpoint returns objects — {name, position, values} — while Liquid returns
          // bare strings. Assuming the Liquid shape rendered "[object Object] 5" on the
          // page. Both are handled rather than one being assumed.
          const opt = product.options[i];
          const name = typeof opt === 'string' ? opt : opt && opt.name;
          return optionWords(name, value);
        })
        .filter(Boolean);
      if (named.length) label = named.join(' · ');
    }
    wrap.appendChild(el('span', 'djh-wishlist__chip-label', label || text(grid, 'unknownVariant') || 'Saved option'));

    if (row.via === 'counter') {
      wrap.appendChild(el('span', 'djh-wishlist__chip-via', text(grid, 'viaCounter') || 'Noted in store'));
    }

    // A lone chip has no remove of its own — Henry, 2026-09-24: the "×" beside a size read as part
    // of the measurement ("25.4 X 2.62 Mm ×"). With one size saved, the heart already removes the
    // piece; a word appears only when there are several sizes and one of them can go.
    if (removable) {
      const drop = el('button', 'djh-wishlist__chip-remove', text(grid, 'removeLabel') || 'Remove');
      drop.type = 'button';
      // The same label the chip shows. A screen reader hearing "Remove option — Size 5" is in
      // the position the sighted customer is in.
      drop.setAttribute('aria-label', (text(grid, 'removeOption') || 'Remove option') + ' — ' + (label || row.variant));
      drop.addEventListener('click', () => store.removeRow(piece.handle, row.variant));
      wrap.appendChild(drop);
    }

    return wrap;
  };

  // The header badge is server-rendered, so an AJAX add leaves it stale — a cart that
  // says 2 while holding 3 is the small wrongness that makes a shop feel unreliable.
  // Reads /cart.js rather than incrementing: the truth is on the server and a piece could
  // already have been in there.
  const refreshCartCount = () => {
    fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart) return;
        document.querySelectorAll('.djh-header__cart-count').forEach((n) => {
          n.textContent = String(cart.item_count);
        });
      })
      .catch(() => { /* the badge stays as it was; nothing here is worth an error to a shopper */ });
  };

  // 🔴 THE RECEIPT HAS TO SURVIVE A REPAINT. The grid is replaced wholesale whenever the
  // list changes or the account answers — `store.ready.then(render)` fires after the proxy
  // replies — so a card that had been swapped for "Added — view cart" came back as a fresh
  // Add to cart button. The customer sees their confirmation vanish and adds a second one.
  //
  // Found by the regression suite on its first run, not by looking at the page: the add
  // and the repaint are seconds apart and whether you see it depends on how fast the proxy
  // answers. That is exactly the kind of bug a person checking by eye signs off.
  //
  // A Set of variant ids, not a flag on the node, because the node does not survive.
  const addedVariants = new Set();

  // ONE BUTTON STYLE PER CARD — Henry, 2026-09-24. A gold "Add to cart" beside an outlined
  // "Choose options" made two cards look like two kinds of thing. Every card now gets the same
  // outlined button and the WORDS say what happens: "Add to cart" when there is nothing left to
  // ask, "Choose your size" when the only thing missing is a size, "Choose options" otherwise.
  const chooseWord = (product) => {
    const names = (product.options || []).map((o) => String(typeof o === 'string' ? o : (o && o.name) || ''));
    const real = names.filter((n) => n && n !== 'Title');
    return real.length === 1 && /size/i.test(real[0])
      ? (text(grid, 'chooseSizeLabel') || 'Choose your size')
      : (text(grid, 'chooseLabel') || 'Choose options');
  };

  const card = (piece, product) => {
    const node = el('article', 'djh-wishlist__card');

    if (!product) {
      node.classList.add('is-gone');
      node.appendChild(el('p', 'djh-wishlist__gone', text(grid, 'goneNote') || 'This piece is no longer on the site. Call us and we will find it.'));
      node.appendChild(el('p', 'djh-wishlist__gone-handle', piece.handle));
      const drop = el('button', 'djh-wishlist__remove', text(grid, 'removeLabel') || 'Remove');
      drop.type = 'button';
      drop.setAttribute('aria-label', (text(grid, 'removeLabel') || 'Remove') + ' ' + piece.handle);
      drop.addEventListener('click', () => store.removePiece(piece.handle));
      node.appendChild(drop);
      return node;
    }

    // The photo is its own box so the corner controls can sit over it rather than over
    // the title — which is where they landed when the link wrapped everything.
    const media = el('div', 'djh-wishlist__media');
    const link = el('a', 'djh-wishlist__link');
    link.href = '/products/' + product.handle;

    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'djh-wishlist__img';
      img.src = product.featured_image;
      img.alt = product.title;
      img.loading = 'lazy';
      link.appendChild(img);
    }
    media.appendChild(link);
    node.appendChild(media);

    const caption = el('a', 'djh-wishlist__caption');
    caption.href = '/products/' + product.handle;
    caption.appendChild(el('p', 'djh-wishlist__title', product.title));
    caption.appendChild(el('p', 'djh-wishlist__price', priceText(product)));
    node.appendChild(caption);

    const sized = piece.rows.filter((r) => {
      if (r.variant === null) return false;
      const v = (product.variants || []).find((x) => String(x.id) === String(r.variant));
      return !isPlaceholderVariant(v, product);
    });
    if (sized.length) {
      const chips = el('div', 'djh-wishlist__chips');
      sized.forEach((row) => chips.appendChild(chip(piece, row, product, sized.length > 1)));
      node.appendChild(chips);
    } else if (piece.rows.some((r) => r.via === 'counter')) {
      // No size chip to hang it on, so the marker goes on the card.
      node.appendChild(el('p', 'djh-wishlist__via', text(grid, 'viaCounter') || 'Noted in store'));
    }

    {
      // ── the heart over the photo, the hint in words under the price ────────────────
      //
      // Henry, 2026-09-08, against Brilliant Earth: a filled heart, top-right of the photo.
      // The heart is REMOVE — it is already filled because the piece is already saved, and
      // pressing a filled heart to unsave is the gesture the PDP's save button teaches.
      //
      // ⚠️ THE ENVELOPE BESIDE IT IS GONE — Henry, 2026-09-24. An 18px envelope over a photo
      // was the page's best idea and its least visible control. "Drop a hint" is now a word
      // under the price, where a customer reading the card meets it.
      const corner = el('div', 'djh-wishlist__corner');

      const hint = el('button', 'djh-wishlist__hint', text(grid, 'hintLink') || 'Drop a hint');
      hint.type = 'button';
      hint.setAttribute('aria-label', (text(grid, 'hintLabel') || 'Drop a hint about') + ' ' + product.title);
      // 🔴 THE OPENER CARRIES THE PIECE. One dialog on the page, four cards; the dialog
      // is told which piece on open rather than one dialog being rendered per card.
      hint.setAttribute('data-djh-hintform-open', '');
      hint.setAttribute('data-handle', product.handle);
      hint.setAttribute('data-title', product.title);
      if (product.featured_image) hint.setAttribute('data-image', product.featured_image);
      hint.setAttribute('data-price', priceText(product));
      if (sized.length === 1 && sized[0].variant) hint.setAttribute('data-variant', sized[0].variant);

      const remove = el('button', 'djh-wishlist__icon djh-wishlist__icon--remove is-saved');
      remove.type = 'button';
      remove.setAttribute('aria-label', (text(grid, 'removeLabel') || 'Remove') + ' ' + product.title);
      remove.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" width="18" height="18">'
        + '<path d="M12 20s-7-4.35-7-9a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 4.65-7 9-7 9z" fill="currentColor"/></svg>';
      remove.addEventListener('click', () => store.removePiece(piece.handle));
      corner.appendChild(remove);

      media.appendChild(corner);
      // Under the price and any size chips — the order a customer reads the card in.
      node.appendChild(hint);
    }

    {
      // ── buy, but only where buying is a single decision ─────────────────────────
      //
      // Henry chose this over BE parity: *"only when there's one variant."* BE puts a
      // Size dropdown and Add To Bag on the card, which means the size picker exists in
      // two places — the defect this repo has paid for repeatedly. A piece with one
      // variant has nothing to ask, so it gets a real Add to Bag; everything else goes to
      // the page that already knows how to ask.
      // 🔴 A SAVED SIZE IS A BUYABLE SIZE — Henry, 2026-09-08: *"ideally, it saves from
      // what the customer selected to be able to buy so we can have that Add to Bag
      // feature."* Exactly right, and it is what makes this work without putting the size
      // picker on the card. The heart now records the variant the customer was looking at
      // (djh-wishlist.js), so the row usually names one — and a row that names a variant
      // has nothing left to ask.
      //
      // ⚠️ ONE SAVED SIZE, NOT SEVERAL. A piece saved in two sizes has two rows and no
      // single answer to "add which one"; those keep "Choose options" and their size chips
      // are the way through. Guessing at the first would be the same class of error as the
      // render-time variant this replaces.
      // 🔴 A ROW SAVED BEFORE 2026-09-08 NAMES A VARIANT NOBODY CHOSE, AND ADD TO BAG
      // WOULD NOW BUY IT. Until today the heart wrote `data-variant` — the RENDER-time
      // default, not the customer's choice — so an existing row can carry size 5 on a
      // piece somebody saved in size 7. That was merely wrong while the card only linked
      // out; it becomes a wrong size in a paid order the moment this button exists.
      //
      // So rows older than the fix are treated as having NO variant: they fall through to
      // "Choose options" and the customer picks on the page. Losing a size we were never
      // sure of costs a click; keeping it costs a resize and a phone call.
      //
      // ⚠️ THE CUTOFF IS A DATE, NOT A FLAG, because the rows already exist in thousands
      // of browsers and no flag can be added to them retroactively. `at` is a millisecond
      // stamp every row has carried since the wishlist shipped.
      var VARIANT_TRUSTED_FROM = Date.UTC(2026, 8, 8, 0, 0, 0);
      var savedRows = piece.rows.filter(function (r) {
        return r.variant && Number(r.at) >= VARIANT_TRUSTED_FROM;
      });
      var savedVariant = savedRows.length === 1
        ? (product.variants || []).find(function (v) { return String(v.id) === String(savedRows[0].variant); })
        : null;

      var only = savedVariant
        || (product.variants && product.variants.length === 1 ? product.variants[0] : null);

      if (only && only.available && addedVariants.has(String(only.id))) {
        const done = el('a', 'djh-wishlist__added', text(grid, 'addedLabel') || 'Added — view cart');
        done.href = '/cart';
        node.appendChild(done);
      } else if (only && only.available) {
        // 🔴 ADDS WITHOUT LEAVING THE PAGE, AND SAYS SO. A wishlist is a list you work
        // through — posting the form navigates to /cart after the first piece and the
        // other four are behind a Back button. And a button that reloads with no visible
        // change is a button people press twice: there is no cart drawer in this theme, so
        // nothing on screen would have moved.
        //
        // ⚠️ THE CONFIRMATION IS THE BUTTON. It becomes "Added — view cart", which is both
        // the receipt and the way onward, and it cannot be double-submitted because the
        // control that would submit is gone. Same shape as the Drop a Hint dialog, which
        // replaces its form rather than leaving an armed button under a success line.
        const form = document.createElement('form');
        form.className = 'djh-wishlist__buy';
        form.action = '/cart/add';
        form.method = 'post';
        const id = document.createElement('input');
        id.type = 'hidden'; id.name = 'id'; id.value = String(only.id);
        const btn = el('button', 'djh-button djh-button--outline djh-wishlist__add', text(grid, 'addLabel') || 'Add to cart');
        btn.type = 'submit';
        form.append(id, btn);

        form.addEventListener('submit', (ev) => {
          // No fetch, no interception — let the browser post it. That is the no-JS path
          // and it still works; this only takes over when fetch is available.
          if (typeof window.fetch !== 'function') return;
          ev.preventDefault();
          btn.disabled = true;
          btn.textContent = text(grid, 'addingLabel') || 'Adding…';

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id: Number(only.id), quantity: 1 }] }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(() => {
              // 🔴 REMEMBER FIRST, THEN REPAINT — do not just swap this node.
              //
              // The grid is rebuilt whenever the account answers, and `render()` runs
              // twice on a normal load. If the add lands between the two, `form` has
              // already been detached and `replaceWith` inserts the receipt into a tree
              // nobody is looking at: the customer sees the button still there, presses
              // it again, and buys two.
              //
              // The regression suite found this on its first run — it clicks the instant
              // the button appears, which is exactly the window a person is too slow to
              // hit. My own hand-check waited three seconds and passed every time.
              addedVariants.add(String(only.id));
              refreshCartCount();
              render();
            })
            .catch(() => {
              // A refusal is real — sold out between load and click, or the item became
              // unavailable. Say so and let them submit again rather than silently
              // pretending; the page is stale, not broken.
              btn.disabled = false;
              btn.textContent = text(grid, 'addFailedLabel') || 'Could not add — try again';
            });
        });

        node.appendChild(form);
      } else if (only && !only.available) {
        // 🔴 A SAVED SIZE THAT SOLD OUT IS ITS OWN ANSWER, not "Choose options". The
        // customer picked this one; telling them to pick again without saying why sends
        // them back to a page to discover it themselves.
        const wrap = el('div', 'djh-wishlist__soldout');
        wrap.appendChild(el('p', 'djh-wishlist__soldout-note', text(grid, 'soldNote') || 'That size has sold out.'));
        const other = el('a', 'djh-button djh-button--outline djh-wishlist__choose', chooseWord(product));
        other.href = '/products/' + product.handle;
        wrap.appendChild(other);
        node.appendChild(wrap);
      } else {
        const choose = el('a', 'djh-button djh-button--outline djh-wishlist__choose', chooseWord(product));
        choose.href = '/products/' + product.handle;
        node.appendChild(choose);
      }
    }

    return node;
  };

  // ── which family a piece belongs to ───────────────────────────────────────────
  //
  // BE groups by category. Ours groups by FAMILY, because that is the axis this shop is
  // organized on and the one a customer already met in the nav — Hawaiian Heirloom is
  // not a category of ring, it is a different kind of thing made by different hands.
  //
  // ⚠️ READ FROM TAGS, WHICH IS WHERE EVERY OTHER SURFACE READS IT. The PDP's trust line,
  // the FAQ set and the recommendations filter all key on `hawaiian-heirloom-*`; a fifth
  // definition here would be the one that disagrees.
  const FAMILIES = [
    { key: 'heirloom', match: (t) => t.some((x) => x.indexOf('hawaiian-heirloom') === 0) },
    { key: 'bridal', match: (t) => t.some((x) => x.indexOf('bridal') === 0) },
    { key: 'bands', match: (t) => t.some((x) => x.indexOf('wedding-bands') === 0) },
    { key: 'fine', match: (t) => t.some((x) => x.indexOf('fine-') === 0) },
  ];

  // 🔴 "OTHER PIECES" WAS A CATEGORY I INVENTED — Henry, 2026-09-08: *"Why does it say
  // 'Other Pieces'? Shouldn't it be aligned to the main category?"* He is right. The shop
  // has four families and "Other" is not one of them; it was my label for a product whose
  // data does not say. Measured: `2-mm-elastic-bead-6-1-2-bracelet-brc999` is live with NO
  // TAGS and an EMPTY product_type — there is nothing to classify it by.
  //
  // ⚠️ SO IT IS NOT NAMED, IT IS UNGROUPED. An unclassifiable piece renders at the end
  // under no heading and gets no tab, rather than being filed under a family the shop does
  // not have. Naming it would put a category on the page that does not exist anywhere else
  // on the site — the same failure as a panel headed "Sizing & fit" on an earring.
  //
  // ⚠️ `product_class` FIRST WHEN IT EXISTS. CATALOG's classifier is the authority and
  // lands with the next publish; `type` is the next-best signal and tags the fallback.
  // Three readers of one fact, in confidence order, rather than one guess.
  const familyOf = (product) => {
    if (!product) return null;

    const cls = String((product.metafields && product.metafields.product_class) || '').toLowerCase();
    if (cls === 'band' || cls === 'ring_other') return 'bands';
    if (cls === 'neck_or_chain' || cls === 'pendant') return 'fine';

    const type = String(product.type || '').toLowerCase();
    if (type.indexOf('heirloom') !== -1) return 'heirloom';
    if (type.indexOf('bridal') !== -1) return 'bridal';
    if (type.indexOf('wedding') !== -1 || type.indexOf('band') !== -1) return 'bands';
    if (type.indexOf('fine') !== -1 || type.indexOf('diamond') !== -1) return 'fine';

    const tags = (product.tags || []).map((t) => String(t).toLowerCase());
    const hit = FAMILIES.find((f) => f.match(tags));
    return hit ? hit.key : null;
  };

  const render = async () => {
    const account = store.isAccount();
    const pieces = group(store.read());

    // The note and the ask both depend on the mode, and the mode is only known after the
    // route has answered — so they are set here rather than by Liquid.
    // "Saved to your account" is a claim about STORAGE and waits for the proxy. Identity
    // alone is not enough to promise it.
    if (note) note.textContent = account ? text(note, 'account') : text(note, 'device');
    // 🔴 SHOWN EVEN ON AN EMPTY LIST, and that is the change of meaning. The old box was
    // hidden until something was saved because "we will keep THESE for you" needs a these.
    // BE's line does not: *"Want to save your wish list? Sign in or create an account to
    // view your saved favorites across all your devices"* is true of an empty page and is
    // the moment somebody is most likely to act on it. Still never shown to a customer
    // already signed in.
    // 🔴 NEVER ASK A SIGNED-IN PERSON TO SIGN IN. Hidden on IDENTITY, not on storage —
    // that is the whole of Henry's (b). A customer Shopify has authenticated should not be
    // reading "Sign in or create an account" whatever the proxy thinks.
    const signedIn = typeof store.isSignedIn === 'function' ? store.isSignedIn() : account;
    if (signin) signin.hidden = signedIn;

    const share = document.querySelector('[data-djh-wishlist-share]');

    if (!pieces.length) {
      showOccasion(null);
      grid.hidden = true;
      grid.replaceChildren();
      if (share) share.hidden = true;
      empty.textContent = account ? text(empty, 'account') : text(empty, 'device');
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    grid.hidden = false;

    const products = await Promise.all(
      pieces.map((piece) =>
        fetch('/products/' + encodeURIComponent(piece.handle) + '.js', { credentials: 'same-origin' })
          .then((r) => (r.ok ? r.json() : null))
          .then((product) =>
            window.djhVariantLookup
              ? window.djhVariantLookup.complete(piece.handle, product, piece.rows.map((r) => r.variant))
              : product)
          .then((product) => (product ? withConfirmedPrice(product) : product))
          .catch(() => null),
      ),
    );

    // ── group, then decide whether grouping is worth showing ────────────────────
    //
    // ⚠️ ONE GROUP GETS NO HEADING. A heading over the only thing on the page is chrome
    // that says nothing. BE has the same restraint on a one-category list.
    //
    // A piece whose product could not be fetched has no family and cannot be grouped;
    // those go last, under no heading, exactly as they render today.
    const rows = pieces.map((piece, i) => ({ piece, product: products[i] }));
    const known = rows.filter((r) => r.product);
    const gone = rows.filter((r) => !r.product);

    const order = [];
    const buckets = new Map();
    const loose = [];
    known.forEach((r) => {
      const k = familyOf(r.product);
      if (!k) { loose.push(r); return; }
      if (!buckets.has(k)) { buckets.set(k, []); order.push(k); }
      buckets.get(k).push(r);
    });

    const label = (k) => text(grid, 'family' + k.charAt(0).toUpperCase() + k.slice(1)) || k;
    const multi = order.length > 1;

    // Share carries the whole list. 🔴 IT STAYS HIDDEN UNTIL ITS ROUTE EXISTS (CATALOG's field
    // traverse, 2026-09-24): it posts to /apps/djh/share-wishlist, which is not built, so a shown
    // button could only fail. The handles are still set so un-hiding is one line when it lands.
    if (share) {
      share.setAttribute('data-handles', known.map((r) => r.product.handle).join(','));
      share.setAttribute('data-count', String(known.length));
    }

    // 🔴 HEADINGS, NOT TABS AS WELL — Henry, 2026-09-24. A tab bar reading "Hawaiian Heirloom (3)"
    // above a heading reading "Hawaiian Heirloom" said the same thing twice. A wishlist is short
    // enough to read top to bottom, so the headings stay and the filter goes.
    const out = [];
    order.forEach((k) => {
      if (multi) out.push(el('h2', 'djh-wishlist__group', label(k)));
      const g = el('div', 'djh-wishlist__grid-inner');
      buckets.get(k).forEach((r) => g.appendChild(card(r.piece, r.product)));
      out.push(g);
    });
    // Unclassifiable pieces and pieces that no longer resolve both land here: at the end,
    // under no heading. Present and unlabelled beats filed under a family that does not exist.
    if (loose.length || gone.length) {
      const g = el('div', 'djh-wishlist__grid-inner');
      loose.forEach((r) => g.appendChild(card(r.piece, r.product)));
      gone.forEach((r) => g.appendChild(card(r.piece, null)));
      out.push(g);
    }
    grid.replaceChildren(...out);
    if (signedIn) nextOccasion().then(showOccasion);
    seedRecommendations(known);
  };

  // ── a date coming up, from My account's own list ─────────────────────────────────
  //
  // THE WISHLIST MEETS THE CALENDAR — Henry, 2026-09-24, the idea he picked as the biggest win.
  // A list is something you look at; a list beside "Leilani's birthday is in 5 days" is a reason
  // to act. The dates are the ones the customer gave on My account, read from the same route and
  // counted in SQL from the shop's today — nothing here counts days (djh-occasions.js).
  //
  // ⚠️ WHOSE DAY DECIDES WHAT THE LINE ASKS. On the customer's OWN birthday the list is what other
  // people should buy, so the action is Share. On a family member's day the list is the customer's
  // own and the question is timing — the same windows My account uses. Offering "share your
  // Wishlist" for somebody else's birthday would be asking them to send their own list as a gift.
  //
  // Signed in only: a signed-out visitor has no dates, and the route would only say so.
  const occasionBox = document.querySelector('[data-djh-wishlist-occasion]');
  // ONE read of the account for the page: the date above and the hints below both come from it.
  let accountAsked = null;
  const readAccount = () => {
    if (!accountAsked) {
      accountAsked = fetch('/apps/djh/account', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((res) => (res && res.signedIn === true && res.account ? res.account : null))
        .catch(() => null);
    }
    return accountAsked;
  };
  const nextOccasion = () => {
    if (!occasionBox || !window.djhOccasions) return Promise.resolve(null);
    return readAccount().then((account) => {
      const sec = account ? account.upcoming : null;
      return sec && sec.kind === 'known' ? window.djhOccasions.next(sec.value) : null;
    });
  };
  const showOccasion = (u) => {
    if (!occasionBox) return;
    if (!u) { occasionBox.hidden = true; return; }
    const O = window.djhOccasions;
    const word = O.occasionWord(u);
    const when = u.daysUntil === 0 ? word + ' is today' : word + ' is in ' + u.daysUntil + ' day' + (u.daysUntil === 1 ? '' : 's');
    occasionBox.querySelector('[data-occasion-when]').textContent = when + ' · ' + O.monthDay(u.date);

    const line = occasionBox.querySelector('[data-occasion-line]');
    const action = occasionBox.querySelector('[data-occasion-action]');
    const share = document.querySelector('[data-djh-wishlist-share]');
    action.hidden = true;
    action.onclick = null;
    if (u.who === 'you') {
      line.textContent = text(occasionBox, 'ownLine');
      if (share && !share.hidden) {
        action.textContent = text(occasionBox, 'ownAction');
        action.removeAttribute('href');
        action.setAttribute('role', 'button');
        action.onclick = (ev) => { ev.preventDefault(); share.click(); };
        action.hidden = false;
      }
    } else {
      const w = O.windowFor(u.daysUntil);
      line.textContent = w ? w[1] : '';
      // The 14-day window's action is this page; offering a link to where they already are is noise.
      if (w && w[3] !== '/pages/wishlist') {
        action.textContent = w[2];
        action.href = w[3];
        action.removeAttribute('role');
        action.hidden = false;
      }
    }
    occasionBox.hidden = false;
  };

  // ── hints you've sent (C11 ruling 5) ─────────────────────────────────────────────
  //
  // Drawn only when the feed carries `hints` as known with at least one; a feed without the field draws nothing, so the
  // section turns on when CATALOG's half lands. Every hint names ONE piece by `pieceHandle` and the tier stores no
  // title (CATALOG ee0bd94c9: `pieceTitle` is null), so the name comes from the product itself, as the saved cards do.
  const hintsBox = document.querySelector('[data-djh-wishlist-hints]');
  const titleOf = (h) => (h.pieceTitle ? Promise.resolve(h.pieceTitle) : !h.pieceHandle ? Promise.resolve(null)
    : fetch(`/products/${encodeURIComponent(h.pieceHandle)}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null)).then((p) => (p && p.title) || null).catch(() => null));
  const showHints = async (account) => {
    const sec = account && account.hints;
    const hints = sec && sec.kind === 'known' && Array.isArray(sec.value) ? sec.value : [];
    if (!hintsBox || !hints.length || !window.djhOccasions) return;
    const day = (on) => window.djhOccasions.monthDay(String(on || '').slice(0, 10));
    const titles = await Promise.all(hints.map(titleOf));
    hintsBox.querySelector('[data-hints-rows]').replaceChildren(...hints.map((h, i) => {
      // Not `djh-wishlist__hint`: that class is the card's "Drop a hint" link (djh-theme.css), underlined as a link.
      const li = el('li', 'djh-wishlist__sent');
      // A piece the store no longer sells keeps its hint: it is named plainly rather than dropped.
      li.appendChild(el('span', 'djh-wishlist__sent-what', `${titles[i] || 'A saved piece'} \u2192 ${h.recipientName || ''}`.trim()));
      const when = day(h.sendOn);
      li.appendChild(el('span', 'djh-wishlist__sent-when', when ? `${h.status === 'sent' ? 'Sent' : 'Sends'} ${when}` : ''));
      return li;
    }));
    hintsBox.hidden = false;
  };

  // (c) The rail, seeded from the newest saved piece. Runs once — re-seeding on every
  // remove would swap the row under somebody mid-scroll for no gain.
  let recsDone = false;
  const seedRecommendations = (rows) => {
    if (recsDone) return;
    const mount = document.querySelector('[data-djh-recs-seed]');
    if (!mount || !rows.length) return;
    const seed = rows[0].product;
    if (!seed || !seed.id) return;
    recsDone = true;

    fetch('/recommendations/products?section_id=djh-product-recommendations&product_id='
      + encodeURIComponent(seed.id) + '&limit=10')
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const rail = doc.querySelector('.djh-recs');
        if (!rail) { mount.remove(); return; }
        mount.replaceChildren(rail);
        mount.hidden = false;
        wireRail(mount);
      })
      .catch(() => { mount.remove(); });
  };

  // The arrows. Same behaviour as the PDP's rail; kept here rather than shared because
  // that file binds on load and this row does not exist yet when it runs.
  const wireRail = (root) => {
    const rail = root.querySelector('[data-djh-recs-rail]');
    const prev = root.querySelector('[data-djh-recs-prev]');
    const next = root.querySelector('[data-djh-recs-next]');
    const nav = root.querySelector('.djh-recs__nav');
    if (!rail || !prev || !next) return;
    const step = () => {
      const first = rail.querySelector('.djh-recs__item');
      return first ? first.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
    };
    const refresh = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      prev.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= max;
      if (nav) nav.hidden = max <= 2;
    };
    prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    rail.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
    refresh();
  };

  document.addEventListener('djh:wishlist:change', render);

  // Paint the local list at once, then again once the account has answered. Waiting for
  // the route before the first paint would leave the page blank on a slow proxy, and on
  // the empty state that blank IS the page.
  render();
  store.ready.then(render);
  readAccount().then(showHints);
  }
})();
